import { useEffect, useRef } from 'react'

/**
 * Pushes a 'page_interactive' event to dataLayer when the page becomes interactive.
 * Triggers on first user interaction, browser idle, or timeout (whichever comes first).
 *
 * Usage in GTM: Create a Custom Event trigger for 'page_interactive' and use it
 * for non-critical tags instead of "All Pages".
 */
export const usePageInteractive = (timeout = 3500): void => {
    const fired = useRef(false)

    useEffect(() => {
        if (typeof window === 'undefined') return

        const events = ['scroll', 'click', 'touchstart', 'keydown']

        const fireEvent = () => {
            if (fired.current) return
            fired.current = true
            window.dataLayer?.push({ event: 'page_interactive' })
            cleanup()
        }

        const cleanup = () => {
            clearTimeout(timeoutId)
            if (idleId !== undefined && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback?.(idleId)
            }
            events.forEach((e) => window.removeEventListener(e, fireEvent))
        }

        events.forEach((e) =>
            window.addEventListener(e, fireEvent, {
                once: true,
                passive: true,
            }),
        )

        const idleId =
            'requestIdleCallback' in window
                ? window.requestIdleCallback?.(fireEvent, { timeout: 2000 })
                : undefined

        const timeoutId = setTimeout(fireEvent, timeout)

        return cleanup
    }, [timeout])
}
